import type { HexMapState } from '../engine/render/renderer';
import { AbstractModal } from './AbstractModal';
import type { ContentBuilder } from './ui/ContentBuilder';
import {
    DEFAULT_CONFIG,
    mergeGameConfig,
    saveGameConfig,
    setGameConfig,
    validateGameConfig,
    type GameConfig,
} from '../config/gameConfig';
import {
    ACTION__SETTINGS__RESET,
    ACTION__SETTINGS__SAVE,
    ACTION__SETTINGS__TAB__AUDIO,
    ACTION__SETTINGS__TAB__CONFIG,
    ACTION__SETTINGS__AUDIO__TOGGLE_BACKGROUND,
    ACTION__SETTINGS__AUDIO__TOGGLE_BELL,
    ACTION__SETTINGS__AUDIO__TOGGLE_HIT,
    ACTION__SETTINGS__AUDIO__SET_BACKGROUND_VOLUME,
    ACTION__SETTINGS__AUDIO__SET_BELL_VOLUME,
    ACTION__SETTINGS__AUDIO__SET_HIT_VOLUME,
} from '../engine/input/actionIds';
import type { Tile } from '../engine/hex/types';
import { t } from '../i18n/translations';

const SETTINGS_TILE: Tile = {
    id: 'settings_modal',
    position: { q: 0, r: 0, s: 0 },
    biome: { type: 'plains' },
    resources: {},
};

export class SettingsModal extends AbstractModal {
    showTileHighlight = false;

    constructor(rect: { x: number; y: number; w: number; h: number }) {
        super(SETTINGS_TILE, rect, null);
        this.id = 'settings_modal';
    }

    getHeaderLabel(_state: HexMapState): string {
        return t(_state.config.ui.language, 'settings.title');
    }

    handleAction(actionId: string, state: HexMapState): boolean {
        switch (actionId) {
            case ACTION__SETTINGS__TAB__CONFIG:
            case ACTION__SETTINGS__TAB__AUDIO:
                this.activeTabId = actionId;
                this.scroll = 0;
                return true;
            case ACTION__SETTINGS__SAVE: {
                try {
                    const parsed = JSON.parse(state.settingsEditor.text) as GameConfig;
                    const merged = mergeGameConfig(DEFAULT_CONFIG, parsed || {});
                    const validationError = validateGameConfig(merged);
                    if (validationError) {
                        state.settingsEditor.error = validationError;
                        return false;
                    }
                    state.config = merged;
                    state.settingsEditor.text = JSON.stringify(merged, null, 2);
                    state.settingsEditor.error = null;
                    setGameConfig(merged);
                    saveGameConfig(merged);
                    return true;
                } catch (_err) {
                    state.settingsEditor.error = { key: 'settings.invalidJson' };
                    return false;
                }
            }
            case ACTION__SETTINGS__RESET: {
                const next = mergeGameConfig(DEFAULT_CONFIG, {});
                state.config = next;
                state.settingsEditor.text = JSON.stringify(next, null, 2);
                state.settingsEditor.error = null;
                setGameConfig(next);
                saveGameConfig(next);
                return true;
            }
            case ACTION__SETTINGS__AUDIO__TOGGLE_BACKGROUND:
                return toggleAudioSetting(state, 'background');
            case ACTION__SETTINGS__AUDIO__TOGGLE_BELL:
                return toggleAudioSetting(state, 'bell');
            case ACTION__SETTINGS__AUDIO__TOGGLE_HIT:
                return toggleAudioSetting(state, 'hit');
            case ACTION__SETTINGS__AUDIO__SET_BACKGROUND_VOLUME:
                return promptAudioVolume(state, 'background');
            case ACTION__SETTINGS__AUDIO__SET_BELL_VOLUME:
                return promptAudioVolume(state, 'bell');
            case ACTION__SETTINGS__AUDIO__SET_HIT_VOLUME:
                return promptAudioVolume(state, 'hit');
            default:
                return false;
        }
    }

    buildContent(state: HexMapState, builder: ContentBuilder): void {
        const lang = state.config.ui.language;
        if (!this.activeTabId) this.activeTabId = ACTION__SETTINGS__TAB__CONFIG;
        builder.tabs(
            [
                {
                    id: ACTION__SETTINGS__TAB__CONFIG,
                    label: t(lang, 'settings.tabConfig'),
                    active: this.activeTabId === ACTION__SETTINGS__TAB__CONFIG,
                },
                {
                    id: ACTION__SETTINGS__TAB__AUDIO,
                    label: t(lang, 'settings.tabAudio'),
                    active: this.activeTabId === ACTION__SETTINGS__TAB__AUDIO,
                },
            ],
            this.tabScroll,
        );
        builder.spacer(6);

        if (this.activeTabId === ACTION__SETTINGS__TAB__CONFIG) {
            builder.text(t(lang, 'settings.configTitle'));
            builder.text(t(lang, 'settings.editHint'), 'muted');
            builder.spacer(6);
            builder.text(t(lang, 'settings.storageHint'), 'muted');
        }

        if (this.activeTabId === ACTION__SETTINGS__TAB__AUDIO) {
            renderAudioSettings(builder, state);
        }
    }
}

function renderAudioSettings(builder: ContentBuilder, state: HexMapState): void {
    const lang = state.config.ui.language;
    const audio = state.config.audio;
    const onLabel = t(lang, 'settings.audio.on');
    const offLabel = t(lang, 'settings.audio.off');
    const formatStatus = (label: string, enabled: boolean, volume: number) =>
        t(lang, 'settings.audio.status', label, enabled ? onLabel : offLabel, volume.toFixed(2));

    builder.section(t(lang, 'settings.audio.title'), (section) => {
        section.text(
            formatStatus(
                t(lang, 'settings.audio.background'),
                audio.background.enabled,
                audio.background.volume,
            ),
        );
        section.button(
            ACTION__SETTINGS__AUDIO__TOGGLE_BACKGROUND,
            t(lang, 'settings.audio.toggle'),
            true,
        );
        section.button(
            ACTION__SETTINGS__AUDIO__SET_BACKGROUND_VOLUME,
            t(lang, 'settings.audio.setVolume'),
            true,
        );
        section.spacer(6);

        section.text(
            formatStatus(t(lang, 'settings.audio.bell'), audio.bell.enabled, audio.bell.volume),
        );
        section.button(
            ACTION__SETTINGS__AUDIO__TOGGLE_BELL,
            t(lang, 'settings.audio.toggle'),
            true,
        );
        section.button(
            ACTION__SETTINGS__AUDIO__SET_BELL_VOLUME,
            t(lang, 'settings.audio.setVolume'),
            true,
        );
        section.spacer(6);

        section.text(
            formatStatus(t(lang, 'settings.audio.hit'), audio.hit.enabled, audio.hit.volume),
        );
        section.button(ACTION__SETTINGS__AUDIO__TOGGLE_HIT, t(lang, 'settings.audio.toggle'), true);
        section.button(
            ACTION__SETTINGS__AUDIO__SET_HIT_VOLUME,
            t(lang, 'settings.audio.setVolume'),
            true,
        );
    });
}

function toggleAudioSetting(state: HexMapState, key: 'background' | 'bell' | 'hit'): boolean {
    const current = state.config.audio[key];
    const nextAudio = {
        ...state.config.audio,
        [key]: { ...current, enabled: !current.enabled },
    };
    const nextConfig = { ...state.config, audio: nextAudio };
    state.config = nextConfig;
    state.settingsEditor.text = JSON.stringify(nextConfig, null, 2);
    setGameConfig(nextConfig);
    saveGameConfig(nextConfig);
    return true;
}

function promptAudioVolume(state: HexMapState, key: 'background' | 'bell' | 'hit'): boolean {
    const lang = state.config.ui.language;
    const current = state.config.audio[key];
    const label = t(lang, `settings.audio.${key}`);
    const next = window.prompt(t(lang, 'settings.audio.volumePrompt', label), `${current.volume}`);
    if (next === null) return false;
    const parsed = Number(next);
    if (!Number.isFinite(parsed)) return false;
    const volume = Math.max(0, Math.min(1, parsed));
    const nextAudio = {
        ...state.config.audio,
        [key]: { ...current, volume },
    };
    const nextConfig = { ...state.config, audio: nextAudio };
    state.config = nextConfig;
    state.settingsEditor.text = JSON.stringify(nextConfig, null, 2);
    setGameConfig(nextConfig);
    saveGameConfig(nextConfig);
    return true;
}
